/**
 * Phase 4 — offline-aware axios wrapper.
 *
 * The default axios instance in lib/auth-client.js handles cookies and
 * 401 redirects. This wrapper adds offline behaviour:
 *
 *   * If the request is a money-moving POST and the device is offline,
 *     enqueue it in IndexedDB and return a synthetic "queued" response
 *     the caller can pattern-match on. The handler shows a toast like
 *     "Saved — will send when you're back online".
 *
 *   * When connectivity returns, drain the queue by replaying each
 *     intent against the server with its original Idempotency-Key. The
 *     Phase 0 idempotency middleware makes replays safe even if the
 *     server already accepted the request (e.g. a tab background-flush
 *     fired before we did).
 *
 *   * Step-up-required actions are NEVER queued — the WebAuthn ceremony
 *     needs a live server challenge. We refuse loudly so the UI prompts
 *     the user to wait for connectivity.
 *
 * The set of "money-moving" endpoints is the same allow-list the Phase 0
 * idempotency middleware guards. Other POSTs (toggles, profile edits)
 * fall through to standard axios.
 */
import axios from 'axios';
import {
  enqueueIntent,
  drainQueue,
  listIntents,
  STATUS,
} from './offline-queue';
import {
  getConnectivity,
  onConnectivityChange,
  probeNow,
} from './connectivity';

/**
 * URL prefixes that are money-moving. Match the Phase 0 middleware
 * (backend/middleware/idempotency.py:GUARDED_PREFIXES). Keep the two
 * lists in lockstep — anything missing here will skip queueing and
 * fail noisily when truly offline.
 */
// Realigned to real routes 2026-08-14 — keep identical to
// backend middleware/idempotency.GUARDED_PREFIXES and auth-client._IDEMPOTENT_PREFIXES.
export const GUARDED_PREFIXES = [
  '/api/wallet/topup',
  '/api/wallet/transfer',
  '/api/limes/subscriptions',
  '/api/limes/order',
  '/api/limes/setup/store-account-customer',
  '/api/postbank/withdraw',
  '/api/postbank/deposit',
  '/api/ace-token',
  '/api/insurance/pay',
  '/api/insurance/collect-premium',
  '/api/insurance/create-policy',
  '/api/payment/card',
  '/api/card/payout',
  '/api/money-transfer',
  '/api/grants/payout',
];

/**
 * Endpoints that REQUIRE a fresh step-up assertion above the per-tier
 * threshold. We refuse to queue these when offline — the WebAuthn /
 * OTP ceremony needs a live server challenge.
 *
 * This is a defensive overlay; the actual decision lives on the server
 * (enforce_step_up dep). The frontend just makes the failure mode nicer.
 */
const STEPUP_LIKELY_PREFIXES = [
  '/api/wallet/transfer',
  '/api/wallet/topup',
  '/api/money-transfer',
  '/api/postbank/withdraw',
  '/api/postbank/deposit',
  '/api/ace-token',
];

function _isGuarded(url) {
  if (!url) return false;
  return GUARDED_PREFIXES.some((p) => url.includes(p));
}

function _isStepUpLikely(url, amountZar) {
  if (!url) return false;
  if (!STEPUP_LIKELY_PREFIXES.some((p) => url.includes(p))) return false;
  // Step-up is amount-gated; if there's no amount in the body, treat as
  // safe-to-queue (e.g. a metadata-only PATCH).
  const n = Number(amountZar);
  if (!Number.isFinite(n) || n <= 0) return false;
  return n > 200;   // conservative client-side guess; server is authoritative
}

/**
 * Synthetic response returned to the caller when a request was queued
 * because the device was offline. Shape mirrors axios's success response
 * so handlers can pattern-match on `.data.queued === true`.
 */
function _queuedResponse(intentId) {
  return {
    status:      202,
    statusText:  'Accepted (queued offline)',
    data:        { queued: true, intent_id: intentId, message: 'Saved — will send when back online' },
    headers:     { 'x-mojo-queued': '1' },
    config:      {},
  };
}

/**
 * Public entry — wrap a single axios call. Most callers don't need
 * this; they can use the convenience post() below.
 */
export async function offlineAwareRequest(config) {
  const url = config.url || '';
  const method = (config.method || 'GET').toUpperCase();

  if (method !== 'POST' || !_isGuarded(url)) {
    // Not a guarded money-moving call — just pass through.
    return axios.request(config);
  }

  const online = getConnectivity().online;
  const amountZar = config?.data?.amount_zar ?? config?.data?.amount;
  if (!online) {
    if (_isStepUpLikely(url, amountZar)) {
      const err = new Error('Step-up authentication is required for this amount. Reconnect to the network to confirm with your passkey.');
      err.code = 'step_up_offline';
      throw err;
    }
    const idempotencyKey = config?.headers?.['Idempotency-Key']
                         || config?.headers?.['idempotency-key']
                         || (typeof crypto !== 'undefined' && crypto.randomUUID
                             ? crypto.randomUUID()
                             : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
    const intent = await enqueueIntent({
      url, method, data: config.data, headers: config.headers || {},
      idempotencyKey,
      action: _pathToAction(url),
      amountZar: Number(amountZar) || null,
      displayedSummary: config?.metadata?.displayedSummary || null,
    });
    return _queuedResponse(intent.id);
  }

  // Online — pass through. The idempotency middleware on the server
  // handles dedup if the user double-clicks.
  return axios.request(config);
}

/**
 * Convenience wrapper for the common case.
 */
export function offlinePost(url, data, config = {}) {
  return offlineAwareRequest({ ...config, method: 'POST', url, data });
}

function _pathToAction(path) {
  // Best-effort mapping; lines up with backend/kyc/product_gates.py
  // PRODUCT_MIN_TIER keys.
  if (!path) return 'unknown';
  if (path.includes('/wallet/transfer'))  return 'wallet.transfer';
  if (path.includes('/wallet/topup'))     return 'wallet.topup';
  if (path.includes('/wallet/send-money'))return 'wallet.send_money';
  if (path.includes('/money-transfer'))   return 'money.transfer';
  if (path.includes('/postbank/withdraw'))return 'postbank.withdraw';
  if (path.includes('/postbank/deposit')) return 'postbank.deposit';
  if (path.includes('/ace/buy'))          return 'ace.buy';
  if (path.includes('/insurance'))        return 'insurance.premium';
  return path;
}

/**
 * Replay one queued intent against the network. The Idempotency-Key the
 * intent was created with is re-attached so the server dedups any
 * duplicate of an already-accepted request.
 */
async function _send(intent) {
  return axios.request({
    url:     intent.url,
    method:  intent.method,
    data:    intent.data,
    headers: { ...(intent.headers || {}), 'Idempotency-Key': intent.idempotencyKey },
  });
}

/**
 * Drain the queue once. Called automatically on connectivity transition
 * to online; also exposed so a "retry now" UI button can trigger it.
 */
export async function flushOfflineQueue() {
  const ok = await probeNow();
  if (!ok) return { sent: 0, succeeded: 0, failed: 0, intents: [] };
  return drainQueue(_send);
}

/**
 * Attach the global connectivity listener — fires flushOfflineQueue()
 * whenever we transition from offline to online. Call once from index.js.
 */
let _started = false;
export function startOfflineQueueAutoFlush() {
  if (_started) return;
  _started = true;
  onConnectivityChange(async (state) => {
    if (!state.online) return;
    try {
      const summary = await flushOfflineQueue();
      if (summary.sent) {
        // eslint-disable-next-line no-console
        console.info('mojo-offline: auto-flush', summary);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('mojo-offline: auto-flush failed', e);
    }
  });
}

/** List of queued intents, used by the dashboard "pending" indicator. */
export async function listQueuedIntents() {
  return (await listIntents(STATUS.QUEUED));
}
